import { Routes, Route } from 'react-router-dom';
import { useEffect, lazy, Suspense } from 'react';
import { AuthRedirects } from './contexts/auth/AuthRedirects';
import { EnhancedAuthSync } from './components/EnhancedAuthSync';
import { Toaster } from '@/components/ui/sonner';
import { ScrollToTop } from './components/ScrollToTop';
import { BusinessFooter } from './components/BusinessFooter';
import { BetaBanner } from './components/BetaBanner';
import { ClientOnlyChatWidget } from './components/chat/ClientOnlyChatWidget';
import { OneSignalProvider } from './providers/OneSignalProvider';
import { BlogPost as BlogPostType } from './types/blog';
import { isSSR } from '@/lib/isSSR';
import { useGoogleAnalytics } from './hooks/useGoogleAnalytics';
import { useStore } from './store/useStore';
import { useSupabaseAuth } from './contexts/auth/SupabaseAuthContext';
import { notificationService } from './services/notifications/notificationService';
import { useAutoPreloader } from './hooks/useMetalPricePreloader';

// Import critical pages directly for SSR to avoid Suspense boundaries
import Index from './pages/Index';
import Blog from './pages/Blog';
import BlogPost from './pages/BlogPost';
import ServerPredictionsPage from './pages/ServerPredictionsPage';
import Dealers from './pages/Dealers';
import DealerDetail from './pages/DealerDetail';
import DealersByCity from './pages/DealersByCity';
import Charts from './pages/Charts';
import Alerts from './pages/Alerts';

// Lazy load non-critical pages to reduce initial bundle size
const LoginPage = lazy(() => import('./pages/auth/login'));
const RegisterPage = lazy(() => import('./pages/auth/register'));
const Verify2FAPage = lazy(() => import('./pages/auth/verify-2fa'));
const ResetPasswordPage = lazy(() => import('./pages/auth/reset-password'));
const Profile = lazy(() => import('./pages/Profile'));
const NotFound = lazy(() => import('./pages/NotFound'));
const Admin = lazy(() => import('./pages/Admin'));
const Gildy = lazy(() => import('./pages/Gildy'));
const Faq = lazy(() => import('./pages/Faq'));
const DebugSupabase = lazy(() => import('./pages/DebugSupabase'));
const NotificationsDemo = lazy(() => import('./pages/NotificationsDemo'));
const RegistrazioneAttivita = lazy(() => import('./pages/RegistrazioneAttivita'));

// Loading component for Suspense fallback
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-gray-900"></div>
  </div>
);

// Add interface for SSR context
interface SSRContext {
  blogPost?: BlogPostType;
  blogPosts?: BlogPostType[];
}

// Add optional SSR context prop
interface AppProps {
  ssrContext?: SSRContext;
}

function App({ ssrContext }: AppProps = {}) {
  // Initialize Google Analytics
  useGoogleAnalytics();

  // Initialize client-side preloading of metal price data
  useAutoPreloader();

  const { fetchCurrentPricesData, fetchFavoriteDealers } = useStore();
  const { isAuthenticated, user } = useSupabaseAuth();
  
  // Set external user ID for authenticated users
  useEffect(() => {
    const setExternalUserId = async () => {
      try {
        if (isAuthenticated && user?.id) {
          await notificationService.setExternalUserId(user.id);
          console.log("[App] External user ID set for notifications");
        }
      } catch (error) {
        console.error("[App] Error setting external user ID:", error);
      }
    };
    
    setExternalUserId();
  }, [isAuthenticated, user]);
  
  // Fetch initial data
  useEffect(() => {
    console.log('[App] Fetching initial data...');
    fetchCurrentPricesData();
    
    // Fetch favorite dealers if the user is authenticated
    if (isAuthenticated && user) {
      console.log("[App] User is authenticated, fetching favorite dealers", user.id);
      fetchFavoriteDealers();
    } else {
      console.log("[App] User not authenticated or user object missing", { isAuthenticated, user });
    }
  }, [fetchCurrentPricesData, fetchFavoriteDealers, isAuthenticated, user]);
  
  // Set up periodic price checks
  useEffect(() => {
    console.log('[App] Setting up periodic price check.');
    const priceCheckInterval = setInterval(() => {
      console.log('[App] Performing periodic price check.');
      fetchCurrentPricesData();
    }, 60000); // Check every minute
    
    return () => clearInterval(priceCheckInterval);
  }, [fetchCurrentPricesData]);
  

  // Initialize blog post data from SSR context if available
  useEffect(() => {
    if (ssrContext?.blogPost && typeof window !== 'undefined') {
      console.log('[App] Initializing blog post data from SSR context');
      // Store post data globally for hydration
      if (typeof window.__BLOG_POST_DATA__ === 'undefined') {
        window.__BLOG_POST_DATA__ = {};
      }
      
      if (window.__BLOG_POST_DATA__ && ssrContext.blogPost?.slug) {
        window.__BLOG_POST_DATA__[ssrContext.blogPost.slug] = ssrContext.blogPost;
        console.log('[App] Set blog post data for slug:', ssrContext.blogPost.slug);
      }
    }
    
    // Initialize blog posts list from SSR context if available
    if (ssrContext?.blogPosts && ssrContext.blogPosts.length > 0 && typeof window !== 'undefined') {
      console.log('[App] Initializing blog posts list from SSR context');
      // Store blog posts list globally for hydration
      window.__BLOG_POSTS = ssrContext.blogPosts;
    }
  }, [ssrContext]);

  return (
    <OneSignalProvider>
      <BetaBanner />
      <ScrollToTop />
      <AuthRedirects />
      <EnhancedAuthSync />
      <div className="flex flex-col min-h-screen pt-8">
        <div className="flex-grow">
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/" element={<Index />} />
              {/* Auth routes */}
              <Route path="/auth/login" element={<LoginPage />} />
              <Route path="/auth/register" element={<RegisterPage />} />
              <Route path="/auth/verify-2fa" element={<Verify2FAPage />} />
              <Route path="/auth/reset-password" element={<ResetPasswordPage />} />
              
              {/* Legacy login route - redirect to new auth route */}
              <Route path="/login" element={<LoginPage />} />
              <Route path="/profilo" element={<Profile />} />
              <Route path="/avvisi" element={<Alerts />} />
              <Route path="/admin" element={<Admin />} />
              <Route path="/grafici" element={<Charts />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/blog/:slug" element={<BlogPost />} />
              <Route path="/gildy" element={<Gildy />} />
              <Route path="/compro-oro" element={<Dealers />} />
              <Route path="/compro-oro/:city" element={<DealersByCity />} />
              <Route path="/compro-oro/:city/:id" element={<DealerDetail />} />
              <Route path="/faq" element={<Faq />} />
              <Route path="/debug-supabase" element={<DebugSupabase />} />
              <Route path="/previsioni" element={<ServerPredictionsPage />} />
              <Route path="/notifiche" element={<NotificationsDemo />} />
              <Route path="/registra-attivita" element={<RegistrazioneAttivita />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </div>
        <BusinessFooter />
      </div>
      <ClientOnlyChatWidget />
      <Toaster />
    </OneSignalProvider>
  );
}

export default App;

// Export for SSR support
export const AppContent = App;

// Add global window interface to handle SSR hydration
declare global {
  interface Window {
    __BLOG_POSTS?: BlogPostType[];
  }
}
